/**
 * Hook for managing optional AI components
 */

import { useCallback, useEffect, useState } from 'react';

import {
  checkInstallStatus,
  getInstallLog,
  getVRAMStatus,
  installComponent as installComponentAPI,
  listComponents,
  loadComponent as loadComponentAPI,
  startComponent as startComponentAPI,
  stopComponent as stopComponentAPI,
  subscribeToInstallProgress,
  uninstallComponent as uninstallComponentAPI,
  unloadComponent as unloadComponentAPI,
} from '../services/componentService';
import type { ComponentInfo, InstallProgress, VRAMStatus } from '../services/componentService';
import { RetryHandler } from '../services/api-adapters/RetryHandler';

import { handleError } from '../utils/errorHandler';

interface UseComponentsResult {
  components: Record<string, ComponentInfo>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  installComponent: (componentId: string) => Promise<(() => void) | undefined>;
  uninstallComponent: (componentId: string) => Promise<void>;
  startComponent: (componentId: string) => Promise<void>;
  stopComponent: (componentId: string) => Promise<void>;
  loadComponent: (componentId: string) => Promise<void>;
  unloadComponent: (componentId: string) => Promise<void>;
  dismissInstallError: (componentId: string) => void;
  fetchInstallLog: (componentId: string) => Promise<string>;
  installProgress: Record<string, InstallProgress>;
  vramStatus: VRAMStatus | null;
  vramLoading: boolean;
}

export function useComponents(): UseComponentsResult {
  const [components, setComponents] = useState<Record<string, ComponentInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [installProgress, setInstallProgress] = useState<Record<string, InstallProgress>>({});
  const [vramStatus, setVramStatus] = useState<VRAMStatus | null>(null);
  const [vramLoading, setVramLoading] = useState(true);

  const fetchVRAM = useCallback(async () => {
    try {
      setVramLoading(true);
      const data = await getVRAMStatus();
      setVramStatus(data);
    } catch (e) {
      console.error('Failed to fetch VRAM status:', e);
    } finally {
      setVramLoading(false);
    }
  }, []);

  const fetchComponents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Retry transparently for cold-backend races (uvicorn still booting,
      // Cloud Run cold start); otherwise a single early failure keeps the
      // components list empty and shows the "No components available" state.
      const data = await RetryHandler.withRetry(() => listComponents());
      setComponents(data);
    } catch (e) {
      console.error('Failed to fetch components:', e);
      setError(handleError(e, 'Components', 'Failed to fetch components'));
    } finally {
      setLoading(false);
    }
    // Also refresh VRAM status when components change
    fetchVRAM();
  }, [fetchVRAM]);

  useEffect(() => {
    fetchComponents();
  }, [fetchComponents]);

  // Poll VRAM status every 5 seconds when there are loaded components
  const hasLoadedComponents = (vramStatus?.components.length ?? 0) > 0;
  useEffect(() => {
    if (!hasLoadedComponents) return;
    const interval = setInterval(fetchVRAM, 5000);
    return () => clearInterval(interval);
  }, [hasLoadedComponents, fetchVRAM]);

  const installComponent = useCallback(
    async (componentId: string) => {
      // Show immediate loading state so the user sees feedback
      setInstallProgress((prev) => ({
        ...prev,
        [componentId]: {
          step: 'starting',
          progress: 0,
          message: 'Starting installation...',
        },
      }));

      try {
        // Start installation first (POST triggers the background install thread).
        // This ensures the backend has progress state before we subscribe to SSE.
        await installComponentAPI(componentId);
      } catch (e) {
        console.error(`Failed to install ${componentId}:`, e);
        const errorMessage =
          e instanceof Error ? e.message : 'Installation failed. Check if the backend is running.';
        setInstallProgress((prev) => ({
          ...prev,
          [componentId]: {
            step: 'error',
            progress: 0,
            message: errorMessage,
          },
        }));
        return;
      }

      // POST succeeded — now subscribe to SSE for real-time progress updates.
      // The backend already has progress state, so SSE will pick it up immediately.
      // Track last step locally so the onComplete closure has the correct terminal step
      let lastStep = 'starting';
      const unsubscribe = subscribeToInstallProgress(
        componentId,
        (progress) => {
          // Ignore "idle" from SSE — install was already confirmed started
          if (progress.step === 'idle') return;
          lastStep = progress.step;
          setInstallProgress((prev) => ({
            ...prev,
            [componentId]: progress,
          }));
        },
        () => {
          // Installation reached terminal state (complete or error) - refresh components
          fetchComponents();
          const isError = lastStep === 'error';
          // Only auto-clear success progress; errors stay visible until user dismisses
          if (!isError) {
            setTimeout(() => {
              setInstallProgress((prev) => {
                const newProgress = { ...prev };
                delete newProgress[componentId];
                return newProgress;
              });
            }, 2000);
          }
        },
        (error) => {
          console.error(`SSE progress error for ${componentId}:`, error);
          // SSE connection failed but install is running in the background.
          // Fall back to polling the status.
          setInstallProgress((prev) => ({
            ...prev,
            [componentId]: {
              step: 'installing',
              progress: 0,
              message: 'Installation in progress (live updates unavailable)',
            },
          }));
          // Poll the install-status endpoint for completion since SSE is down
          const pollInterval = setInterval(async () => {
            try {
              const status = await checkInstallStatus(componentId);

              // Update progress with latest backend state
              if (status.step !== 'idle') {
                setInstallProgress((prev) => ({
                  ...prev,
                  [componentId]: {
                    step: status.step,
                    progress: status.progress,
                    message: status.message,
                  },
                }));
              }

              // Installation reached terminal state (complete or error)
              if (status.done) {
                clearInterval(pollInterval);
                fetchComponents();
                // Only auto-clear success; errors stay visible until user dismisses
                if (status.step !== 'error') {
                  setTimeout(() => {
                    setInstallProgress((prev) => {
                      const updated = { ...prev };
                      delete updated[componentId];
                      return updated;
                    });
                  }, 2000);
                }
              }
            } catch {
              // ignore poll errors — backend may be temporarily busy
            }
          }, 5000);
          // Stop polling after 90 minutes (covers longest install timeout of 60min + buffer)
          setTimeout(() => {
            clearInterval(pollInterval);
            setInstallProgress((prev) => {
              const current = prev[componentId];
              if (current && current.step !== 'complete' && current.step !== 'error') {
                return {
                  ...prev,
                  [componentId]: {
                    step: 'error',
                    progress: 0,
                    message: 'Installation status check timed out. Check backend logs.',
                  },
                };
              }
              return prev;
            });
          }, 5400000);
        }
      );

      return unsubscribe;
    },
    [fetchComponents]
  );

  const uninstallComponent = useCallback(
    async (componentId: string) => {
      try {
        await uninstallComponentAPI(componentId);
        await fetchComponents();
      } catch (e) {
        console.error(`Failed to uninstall ${componentId}:`, e);
        throw e;
      }
    },
    [fetchComponents]
  );

  const startComponent = useCallback(
    async (componentId: string) => {
      try {
        await startComponentAPI(componentId);
        await fetchComponents();
      } catch (e) {
        console.error(`Failed to start ${componentId}:`, e);
        throw e;
      }
    },
    [fetchComponents]
  );

  const stopComponent = useCallback(
    async (componentId: string) => {
      try {
        await stopComponentAPI(componentId);
        await fetchComponents();
      } catch (e) {
        console.error(`Failed to stop ${componentId}:`, e);
        throw e;
      }
    },
    [fetchComponents]
  );

  const loadComponent = useCallback(
    async (componentId: string) => {
      try {
        await loadComponentAPI(componentId);
        await fetchComponents();
      } catch (e) {
        console.error(`Failed to load ${componentId}:`, e);
        throw e;
      }
    },
    [fetchComponents]
  );

  const unloadComponent = useCallback(
    async (componentId: string) => {
      try {
        await unloadComponentAPI(componentId);
        await fetchComponents();
      } catch (e) {
        console.error(`Failed to unload ${componentId}:`, e);
        throw e;
      }
    },
    [fetchComponents]
  );

  const dismissInstallError = useCallback((componentId: string) => {
    setInstallProgress((prev) => {
      const updated = { ...prev };
      delete updated[componentId];
      return updated;
    });
  }, []);

  const fetchInstallLog = useCallback(async (componentId: string): Promise<string> => {
    try {
      const data = await getInstallLog(componentId);
      return data.log || 'No log available.';
    } catch {
      return 'Failed to retrieve installation log.';
    }
  }, []);

  return {
    components,
    loading,
    error,
    refresh: fetchComponents,
    installComponent,
    uninstallComponent,
    startComponent,
    stopComponent,
    loadComponent,
    unloadComponent,
    dismissInstallError,
    fetchInstallLog,
    installProgress,
    vramStatus,
    vramLoading,
  };
}
